/**
 * Shopper AI (Support) specific keyboard shortcuts and scopes.
 * This scope tree is nested under "shopperAi" in the global registry.
 */

import type { KeyboardShortcut } from "./keyboard-shortcut";

/**
 * Shopper AI scope tree.
 * These scopes are nested under the shopperAi root scope in the global registry.
 */
export const shopperAiScopes = [
  /** actionMenu scope - main action shortcuts */
  "actionMenu",
  /** Conversation table navigation - j/k to navigate, Enter to open */
  "conversationTable",
  /** Priority selection submenu */
  "priority",
  /** Assignee selection submenu */
  "assign",
  /** Tag selection submenu */
  "addTag",
  /** Snooze time selection submenu */
  "snooze",
  /** Message input - send and draft management */
  "messageInput",
] as const;

export type ShopperAiScope = (typeof shopperAiScopes)[number];

/** Shopper AI specific keyboard shortcut with scope */
export type ShopperAiShortcut = KeyboardShortcut<ShopperAiScope>;

/**
 * Conversation action keyboard shortcuts.
 * actionMenu-level actions use simple keys (activated when menu is open).
 */
export const conversationActionShortcuts = {
  CLOSE: { code: "KeyC", keyLabel: "C", scope: "actionMenu" },
  REOPEN: { code: "KeyO", keyLabel: "O", scope: "actionMenu" },
  SNOOZE: { code: "KeyS", keyLabel: "S", scope: "actionMenu" },
  ASSIGN: { code: "KeyA", keyLabel: "A", scope: "actionMenu" },
  MARK_READ: { code: "KeyR", keyLabel: "R", scope: "actionMenu" },
  MARK_UNREAD: { code: "KeyU", keyLabel: "U", scope: "actionMenu" },
  SET_PRIORITY: { code: "KeyP", keyLabel: "P", scope: "actionMenu" },
  ADD_TAG: { code: "KeyT", keyLabel: "T", scope: "actionMenu" },
  ARCHIVE: { code: "KeyE", keyLabel: "E", scope: "actionMenu" },
  DELETE: { code: "KeyD", keyLabel: "D", scope: "actionMenu" },
} as const satisfies Record<string, ShopperAiShortcut>;

export const priorityShortcuts = {
  PRIORITY_LOW: { code: "KeyL", keyLabel: "L", scope: "priority" },
  PRIORITY_MEDIUM: { code: "KeyM", keyLabel: "M", scope: "priority" },
  PRIORITY_HIGH: { code: "KeyH", keyLabel: "H", scope: "priority" },
  PRIORITY_CRITICAL: { code: "KeyC", keyLabel: "C", scope: "priority" },
  PRIORITY_CLEAR: { code: "KeyX", keyLabel: "X", scope: "priority" },
} as const satisfies Record<string, ShopperAiShortcut>;

export const snoozeShortcuts = {
  SNOOZE_1H: { code: "Digit1", keyLabel: "1", scope: "snooze" },
  SNOOZE_4H: { code: "Digit2", keyLabel: "2", scope: "snooze" },
  SNOOZE_24H: { code: "Digit3", keyLabel: "3", scope: "snooze" },
  SNOOZE_CUSTOM: { code: "Digit4", keyLabel: "4", scope: "snooze" },
  CLEAR_SNOOZE: { code: "KeyX", keyLabel: "X", scope: "snooze" },
} as const satisfies Record<string, ShopperAiShortcut>;

/**
 * Table navigation shortcuts for the conversation list.
 * Vim-style navigation keys without modifiers.
 */
export const tableNavigationShortcuts = {
  NAVIGATE_DOWN: { code: "KeyJ", keyLabel: "J", scope: "conversationTable" },
  NAVIGATE_UP: { code: "KeyK", keyLabel: "K", scope: "conversationTable" },
  OPEN_CONVERSATION: {
    code: "Enter",
    keyLabel: "Enter",
    scope: "conversationTable",
  },
  OPEN_ACTION_MENU: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["alt"],
    scope: "conversationTable",
  },
} as const satisfies Record<string, ShopperAiShortcut>;

export const messageInputShortcuts = {
  SEND_MESSAGE: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
  DELETE_DRAFT: {
    code: "Backspace",
    keyLabel: "Backspace",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
} as const satisfies Record<string, ShopperAiShortcut>;

export type ConversationActionShortcutId =
  keyof typeof conversationActionShortcuts;

export type TableNavigationShortcutId = keyof typeof tableNavigationShortcuts;
